/**
 * Suggest a Club - Allows users to propose new club ideas
 * New clubs start as sub-groups within AADC and can graduate to full clubs
 * based on community interest and engagement
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  ChevronLeft, 
  Lightbulb, 
  Users, 
  TrendingUp, 
  Award,
  Globe,
  MapPin,
  Heart
} from 'lucide-react';
import { toast } from 'sonner';
import usePageTitle from '../hooks/usePageTitle';

const API = process.env.REACT_APP_BACKEND_URL;

// Club suggestion categories - maps to subgroup types
const CLUB_CATEGORIES = [
  { value: 'breed', label: 'Breed Club', emoji: '🐕', description: 'For a specific dog breed (e.g., "Golden Retriever Club")' },
  { value: 'location', label: 'Regional Club', emoji: '📍', description: 'For a city, state, or country (e.g., "Texas Dog Owners")' },
  { value: 'activity', label: 'Activity Club', emoji: '🏃', description: 'For a dog sport or activity (e.g., "Agility Training Club")' },
  { value: 'rescue', label: 'Rescue Network', emoji: '🏠', description: 'For rescue organizations or adoption advocates' },
  { value: 'health', label: 'Health Focus', emoji: '🏥', description: 'For health conditions or special needs (e.g., "Senior Dogs Club")' },
  { value: 'showcase', label: 'Show & Competition', emoji: '🏆', description: 'For conformation shows or competitions' },
  { value: 'general', label: 'Special Interest', emoji: '⭐', description: 'Something else? Tell us about it!' }
];

const COLOR_OPTIONS = [
  { value: '#c9762b', label: 'AADC Brown' },
  { value: '#AA151B', label: 'Spanish Red' },
  { value: '#1e3a5f', label: 'Navy Blue' },
  { value: '#22c55e', label: 'Green' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#f97316', label: 'Orange' },
  { value: '#06b6d4', label: 'Teal' },
];

const EMOJI_OPTIONS = ['🐕', '🐶', '🐩', '🐾', '🦴', '❤️', '⭐', '🏆', '📍', '🏥', '🏠', '🎉', '🌎', '🇺🇸'];

const SuggestClub = () => {
  usePageTitle('Suggest a Club');
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [aadcClubId, setAadcClubId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    icon_emoji: '🐾',
    color: '#c9762b',
    why_needed: '',
    target_audience: '',
    similar_communities: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: '/suggest-club' } });
      return;
    }
    fetchAADCClub();
  }, [user, navigate]);

  const fetchAADCClub = async () => {
    try {
      const response = await axios.get(`${API}/api/clubs/all-american-dog-club`);
      setAadcClubId(response.data.id);
    } catch (error) {
      console.error('Error fetching AADC club:', error);
      toast.error('Unable to load club information');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!aadcClubId) {
      toast.error('Unable to create suggestion - club not found');
      return;
    }

    setLoading(true);
    try {
      
      // Create as a sub-group under AADC with special "club_suggestion" tag
      const subgroupData = {
        name: `💡 ${formData.name}`,
        description: `**Club Suggestion**\n\n${formData.description}\n\n**Why this club is needed:**\n${formData.why_needed}\n\n**Target audience:**\n${formData.target_audience}${formData.similar_communities ? `\n\n**Similar communities:**\n${formData.similar_communities}` : ''}`,
        club_id: aadcClubId,
        subgroup_type: formData.category,
        icon_emoji: formData.icon_emoji,
        color: formData.color,
        visibility: 'public',
        tags: ['club_suggestion', 'incubator', formData.category],
        rules: [
          'This is a club suggestion in incubation',
          'Join to show your support and help it grow!',
          'Share ideas for what this club should offer',
          'Be respectful and constructive'
        ]
      };

      const response = await axios.post(`${API}/api/subgroups`, subgroupData);

      toast.success('Club suggestion created! Share it to gather support.');
      navigate(`/communities/${response.data.id}`);
    } catch (error) {
      console.error('Error creating club suggestion:', error);
      const message = error.response?.data?.detail || 'Failed to create suggestion';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const selectedCategory = CLUB_CATEGORIES.find(c => c.value === formData.category);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <Lightbulb className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Suggest a New Club</h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Have an idea for a club that doesn't exist yet? Start as a community, 
            grow your membership, and graduate to a <strong>fully independent club</strong> with 
            your own URL, bylaws, and policies.
          </p>
        </div>

        {/* How It Works */}
        <Card className="mb-8 bg-muted/30">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Your Path to a Standalone Club
            </h3>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-primary">1</span>
                </div>
                <div>
                  <p className="font-medium">Submit Idea</p>
                  <p className="text-sm text-muted-foreground">Start as a community</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-primary">2</span>
                </div>
                <div>
                  <p className="font-medium">Build Support</p>
                  <p className="text-sm text-muted-foreground">Grow to 50+ members</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-primary">3</span>
                </div>
                <div>
                  <p className="font-medium">Graduate</p>
                  <p className="text-sm text-muted-foreground">Become a full club</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                  <Award className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Independence</p>
                  <p className="text-sm text-muted-foreground">Own URL, bylaws & policies</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>Club Details</CardTitle>
            <CardDescription>Tell us about your club idea</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Club Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Club Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Pacific Northwest Dog Hikers"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  maxLength={80}
                  data-testid="club-name-input"
                />
                <p className="text-xs text-muted-foreground">
                  {formData.name.length}/80 characters
                </p>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label>Club Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => {
                    const cat = CLUB_CATEGORIES.find(c => c.value === value);
                    setFormData({ 
                      ...formData, 
                      category: value,
                      icon_emoji: cat?.emoji || formData.icon_emoji
                    });
                  }}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLUB_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div className="flex items-center gap-2">
                          <span>{cat.emoji}</span>
                          <span>{cat.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedCategory && (
                  <p className="text-sm text-muted-foreground">{selectedCategory.description}</p>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="What is this club about? What will members get from joining?"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  maxLength={500}
                  data-testid="description-input"
                />
                <p className="text-xs text-muted-foreground">
                  {formData.description.length}/500 characters
                </p>
              </div>

              {/* Why Needed */}
              <div className="space-y-2">
                <Label htmlFor="why_needed">Why is this club needed? *</Label>
                <Textarea
                  id="why_needed"
                  placeholder="What gap does this fill? Why would people want to join?"
                  value={formData.why_needed}
                  onChange={(e) => setFormData({ ...formData, why_needed: e.target.value })}
                  rows={3}
                  maxLength={300}
                  data-testid="why-needed-input"
                />
              </div>

              {/* Target Audience */}
              <div className="space-y-2">
                <Label htmlFor="target_audience">Who is this for?</Label>
                <Input
                  id="target_audience"
                  placeholder="e.g., Dog owners in the Pacific Northwest who love hiking"
                  value={formData.target_audience}
                  onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                  maxLength={150}
                  data-testid="audience-input"
                />
              </div>

              {/* Icon & Color */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Icon</Label>
                  <div className="flex flex-wrap gap-2">
                    {EMOJI_OPTIONS.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon_emoji: emoji })}
                        className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center border-2 transition-all ${
                          formData.icon_emoji === emoji 
                            ? 'border-primary bg-primary/10' 
                            : 'border-muted hover:border-primary/50'
                        }`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Theme Color</Label>
                  <div className="flex flex-wrap gap-2">
                    {COLOR_OPTIONS.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, color: color.value })}
                        className={`w-10 h-10 rounded-lg border-2 transition-all ${
                          formData.color === color.value 
                            ? 'border-foreground scale-110' 
                            : 'border-transparent hover:scale-105'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.label}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Similar Communities (optional) */}
              <div className="space-y-2">
                <Label htmlFor="similar_communities">
                  Similar communities elsewhere? (optional)
                </Label>
                <Input
                  id="similar_communities"
                  placeholder="e.g., r/hiking, Facebook Hiking with Dogs group"
                  value={formData.similar_communities}
                  onChange={(e) => setFormData({ ...formData, similar_communities: e.target.value })}
                  maxLength={200}
                />
                <p className="text-xs text-muted-foreground">
                  Helps us understand the potential audience
                </p>
              </div>

              {/* Preview */}
              <div className="pt-4 border-t">
                <Label className="mb-3 block">Preview</Label>
                <div 
                  className="p-4 rounded-xl flex items-center gap-4"
                  style={{ backgroundColor: `${formData.color}15` }}
                >
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl"
                    style={{ backgroundColor: `${formData.color}30` }}
                  >
                    {formData.icon_emoji}
                  </div>
                  <div>
                    <h3 className="font-semibold">
                      💡 {formData.name || 'Your Club Name'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {formData.description?.slice(0, 80) || 'Club description...'}{formData.description?.length > 80 ? '...' : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !formData.name || !formData.description || !formData.category}
                  className="flex-1"
                  data-testid="submit-suggestion-btn"
                >
                  {loading ? 'Creating...' : 'Submit Suggestion'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Info Alert */}
        <Alert className="mt-6">
          <Heart className="h-4 w-4" />
          <AlertDescription>
            <strong>Your path to independence:</strong> Your club suggestion starts as a community. 
            Once you reach 50+ engaged members, you can apply to graduate to a <strong>fully independent club</strong> with:
            <ul className="mt-2 ml-4 list-disc text-sm space-y-1">
              <li>Your own custom URL (e.g., yourclub.worldpets.club)</li>
              <li>Custom bylaws and membership policies</li>
              <li>Your own branding, colors, and identity</li>
              <li>Independent governance and admin controls</li>
            </ul>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
};

export default SuggestClub;
